import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import StandardModal from '@/components/common/standard.modal';
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useRemoveEmailOptOutMutation } from '@/state/mutations/email-preferences/use-remove-email-opt-out';
import { useSilenceEmailOptOut } from '@/state/mutations/email-preferences/use-silence-email-opt-out';
import { useNotifications } from '@/hooks/use-notifications';
import type { EmailOptOutDto } from '@/state/queries/email-preferences/use-get-email-opt-outs';

const UnblockOrgModal = ({
  open,
  onClose,
  optOuts,
}: {
  open: boolean;
  onClose: () => void;
  optOuts: EmailOptOutDto[];
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(() => optOuts.map((o) => o.id));
  const removeOptOut = useRemoveEmailOptOutMutation();
  const silenceOptOut = useSilenceEmailOptOut();

  const organizationName = optOuts[0]?.organization.name ?? '';
  const hasMultipleEmails = optOuts.length > 1;
  const isPending = removeOptOut.isPending || silenceOptOut.isPending;
  const hasSelection = selectedIds.length > 0;

  const optOutIdsKey = optOuts.map((o) => o.id).join(',');

  useEffect(() => {
    if (!open) return;
    setDontShowAgain(false);
    setSelectedIds(optOutIdsKey.length > 0 ? optOutIdsKey.split(',') : []);
  }, [open, optOutIdsKey]);

  const toggleEmail = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]));
  };

  const handleUnblockNow = async () => {
    if (!hasSelection) return;
    try {
      await Promise.all(selectedIds.map((id) => removeOptOut.mutateAsync(id)));
      onClose();
    } catch {
      notification.notifyError(t('emailPreferences.unblockOrgModal.errorUnblock'));
    }
  };

  const handleNotNow = async () => {
    try {
      if (dontShowAgain && hasSelection) {
        await Promise.all(selectedIds.map((id) => silenceOptOut.mutateAsync(id)));
      }
      onClose();
    } catch {
      notification.notifyError(t('emailPreferences.unblockOrgModal.errorSilence'));
    }
  };

  const subtitle = hasMultipleEmails
    ? t('emailPreferences.unblockOrgModal.textMultiple', { organization: organizationName })
    : t('emailPreferences.unblockOrgModal.text', {
        organization: organizationName,
        email: optOuts[0]?.email ?? '',
      });

  return (
    <StandardModal
      open={open}
      close={onClose}
      title={t('emailPreferences.unblockOrgModal.title')}
      subtitle={subtitle}
      size="md"
      menu={false}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {hasMultipleEmails && (
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {t('emailPreferences.unblockOrgModal.selectEmails')}
            </Typography>
            {optOuts.map((optOut) => (
              <FormControlLabel
                key={optOut.id}
                control={
                  <Checkbox
                    checked={selectedIds.includes(optOut.id)}
                    onChange={() => toggleEmail(optOut.id)}
                    size="small"
                    disabled={isPending}
                  />
                }
                label={optOut.email}
                slotProps={{ typography: { variant: 'body2' } }}
              />
            ))}
          </Stack>
        )}
        <FormControlLabel
          control={
            <Checkbox checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} size="small" />
          }
          label={t('emailPreferences.unblockOrgModal.dontShowAgain')}
          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
        />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" color="info" onClick={handleNotNow} disabled={isPending}>
            {t('emailPreferences.unblockOrgModal.notNow')}
          </Button>
          <Button variant="contained" color="info" onClick={handleUnblockNow} disabled={isPending || !hasSelection}>
            {t('emailPreferences.unblockOrgModal.unblockNow')}
          </Button>
        </Box>
      </Stack>
    </StandardModal>
  );
};

export default UnblockOrgModal;
